import React, { useState } from 'react';
import { useEvaluations, useDeleteEvaluation } from '../../hooks/useEvaluations';
import { EvaluationRunner } from './EvaluationRunner';
import { EvaluationList } from './EvaluationList';
import { EvaluationViewer } from './EvaluationViewer';
import type { Evaluation } from '../../types';

interface EvaluationsTabProps {
  projectId: string;
}

export const EvaluationsTab: React.FC<EvaluationsTabProps> = ({ projectId }) => {
  const { evaluations, loading } = useEvaluations(projectId);
  const { deleteEvaluation } = useDeleteEvaluation();

  const [showRunner, setShowRunner] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState<Evaluation | null>(null);

  const handleComplete = () => {
    setShowRunner(false);
  };

  const handleDelete = async (evaluationId: string) => {
    try {
      await deleteEvaluation(evaluationId);
    } catch (err) {
      console.error('Failed to delete evaluation:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Evaluations</h2>
          <p className="mt-1 text-sm text-gray-500">
            Evaluate creative assets with your personas
          </p>
        </div>
        <button
          onClick={() => setShowRunner(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-500 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          New Evaluation
        </button>
      </div>

      {/* Evaluation List */}
      <EvaluationList
        evaluations={evaluations}
        onSelect={setSelectedEvaluation}
        onDelete={handleDelete}
      />

      {/* Modals */}
      {showRunner && (
        <EvaluationRunner
          projectId={projectId}
          onComplete={handleComplete}
          onCancel={() => setShowRunner(false)}
        />
      )}

      {selectedEvaluation && (
        <EvaluationViewer
          evaluation={selectedEvaluation}
          onClose={() => setSelectedEvaluation(null)}
        />
      )}
    </div>
  );
};
